import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, IconButton, Menu, MenuItem, Stack
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { ClickableCard, ClickableCardBase, HoverPreview, ImageClipper, ImageOuter } from './VideoListItem.styled';
import { VideoListItemProps } from './VideoListItem.types';
import { WithRelatedVideos } from '../../../../api/interfaces/Video';
import { getVideoById } from '../../../../api/services/video';
import { handleApiError } from '../../../../utils/errorHandler';
import { pushNotification } from '../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { PinnacleDialog } from '../../../../components/PinnacleDialog';
import { SkeletonSubjectDialog } from '../../../../components/skeleton/SkeletonSubjectDialog/SkeletonSubjectDialog';
import { SkeletonVideoList } from '../../../../components/skeleton/SkeletonVideoList/SkeletonVideoList';
import MarkAsWatchedButton from '../../../../components/common/MarkAsWatchedButton/MarkAsWatchedButton';
import SortOutlinedIcon from '@mui/icons-material/SortOutlined';
import { PinnacleChip } from '../../../../components/PinnacleChip';
import getTheme from '../../../../theme/theme';
import { PinnacleThemes } from '../../../../theme/types';
import PinnacleVideoThumbnail from '../../../../components/PinnacleVideoThumbnail/PinnacleVideoThumbnail';
import { E_CANNOT_MARK_AS_WATCHED } from '../../../../utils/constants';
import { isVideoPlayable } from '../../../../utils/videoPlayable';
import { useNavigate, useParams } from 'react-router-dom';
import { watchVideoRedirect } from '../../../../utils/watchVideoRedirect';

const VideoListItem: React.FC<VideoListItemProps> = ({
  keyIndex,
  thumbnail,
  video,
  title,
  duration,
  isRecentlyWatch,
  isAccountTrial
}) => {
  const theme = getTheme(PinnacleThemes.BASE_THEME);
  const navigate = useNavigate();

  const { videoId: paramVideoId } = useParams<{ videoId?: string }>();
  const isOpen = paramVideoId === video._id;

  // const [isOpen, setIsOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<WithRelatedVideos | null>(null);
  const [isSorting, setIsSorting] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [videosState, setVideosState] = useState<WithRelatedVideos['relatedVideos']>([]);
  // const [isAccountTrial, setIsAccountTrial] = useState<boolean | null>(null);

  // const validateAccountTrial = async () => { 
  //   const profileResponse = await profile();
  //   const userProfile = profileResponse.data.user;
  //   if (!isAccountTrial || isAccountTrial !== userProfile.isTrial) {
  //     // Sync the account trial status
  //     setIsAccountTrial(userProfile.isTrial ?? false);
  //   }
  // }

  const handleVideoInfoPrompt = () => {
    if (isRecentlyWatch) {
      watchVideoRedirect(navigate, video, true);
    } else {
      navigate(`/videos/list/${video._id}`);
    }
  };

  // const handleVideoInfoPrompt = async () => {
  //   if (isRecentlyWatch) {
  //     watchVideoRedirect(navigate, video, true);
  //   } else {
  //     // setIsOpen(true);
  //     try {
  //       setSelectedVideo(null);
  //       if (video && video._id) {
  //         handleShowAll(video._id)
  //         await validateAccountTrial();
  //         const currentVideo = await getVideoById(video?._id);
  //         setSelectedVideo(currentVideo.data);
  //         setVideosState(
  //           currentVideo.data.relatedVideos.map(v => ({ ...v, isLoading: false }))
  //         );
  //       }
  //     } catch (error) {
  //       const { errorMessage } = handleApiError(error, { redirectOnAuthError: true });
  //       pushNotification({ details: errorMessage, type: NotificationTypes.Error });
  //     }
  //   }
  // };

  const handleSort = async (sortBy: string, orderBy: 'ASC' | 'DESC') => {
    setIsSorting(true);
    setAnchorEl(null);
    try {
      if (!selectedVideo?.video._id) {
        pushNotification({
          details: 'Video ID is missing.',
          type: NotificationTypes.Error,
        });
        setIsSorting(false);
        return;
      }
      // await validateAccountTrial();
      const response = await getVideoById(selectedVideo.video._id, { sortBy, orderBy });
      if (response.result === 'success') {
        setVideosState(
          response.data.relatedVideos.map(v => ({ ...v, isLoading: false }))
        );
      }
    } catch (err) {
      handleApiError(err, {
        notify: (msg) => pushNotification({ details: msg, type: NotificationTypes.Error }),
      });
    } finally {
      setIsSorting(false);
    }
  };

  const handleClose = () => {
    navigate("/");
  };

  // const fetchVideo = useCallback(async (videoId: string) => {
  //   try {
  //     setSelectedVideo(null);
  //     // await validateAccountTrial();
  //     const currentVideo = await getVideoById(videoId);
  //     setSelectedVideo(currentVideo.data);
  //     setVideosState(
  //       currentVideo.data.relatedVideos.map(v => ({ ...v, isLoading: false }))
  //     );
  //   } catch (error) {
  //     const { errorMessage } = handleApiError(error, { redirectOnAuthError: true });
  //     pushNotification({ details: errorMessage, type: NotificationTypes.Error });
  //   }
  // }, []);
  const lastFetchedId = useRef<string | null>(null);

  const fetchVideo = useCallback(async (videoId: string) => {
    if (lastFetchedId.current === videoId) return;
    lastFetchedId.current = videoId;

    try {
      const currentVideo = await getVideoById(videoId);
      setSelectedVideo(currentVideo.data);
      setVideosState(
        currentVideo.data.relatedVideos.map(v => ({ ...v, isLoading: false }))
      );
    } catch (error) {
      const { errorMessage } = handleApiError(error, { redirectOnAuthError: true });
      pushNotification({ details: errorMessage, type: NotificationTypes.Error });
    }
  }, []);

  useEffect(() => {
    if (!paramVideoId) return;
    if (paramVideoId !== video._id) return; // only the right item fetches

    let cancelled = false;
    const init = async () => {
      await fetchVideo(paramVideoId);
      if (!cancelled) {
        // set state here
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [paramVideoId, fetchVideo, video._id]);

  return (
    <>
      {/* Video Thumbnail */}
      <Box key={keyIndex}>
        <ClickableCardBase onClick={handleVideoInfoPrompt}>
          <ClickableCard>
            <ImageOuter>
              <ImageClipper>
                <PinnacleVideoThumbnail src={thumbnail} alt={title} />
              </ImageClipper>

              <HoverPreview className="hoverPreview" align="left">
                <img src={video.thumbnail} alt={video.title} />
              </HoverPreview>

            </ImageOuter>
            <Box sx={{ textAlign: 'left', mt: 1, width: '100%' }}>
              <Typography
                variant="h6"
                sx={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                }}
              >
                {title}
              </Typography>
              <Typography variant="caption">{duration}</Typography>
            </Box>
          </ClickableCard>
        </ClickableCardBase>
      </Box>

      {/* Dialog for video details and related videos */}
      <PinnacleDialog
        isOpen={isOpen}
        setIsOpen={handleClose}
        disableBackDropClick
        headerTitle={title}
      >
        <Box>
          {!selectedVideo ? (
            <SkeletonSubjectDialog />
          ) : (
            <>
              {/* Video cover */}
              <Box position="relative">
                <Box
                  component="img"
                  src={selectedVideo.video.thumbnail}
                  alt={selectedVideo.video.title}
                  height="100%"
                  sx={{
                    width: '100%',
                    borderRadius: '12px',
                    objectFit: 'cover',
                    filter: !isVideoPlayable(isAccountTrial ?? false, selectedVideo.video.isTrial ?? false, (selectedVideo.video.videoUrl ?? '')  || (selectedVideo.video.videoUrl1 ?? '') || (selectedVideo.video.videoUrl2 ?? '')) ? 'brightness(40%)' : 'none',
                    transition: 'filter 0.2s'
                  }}
                />
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  sx={{
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none'
                  }}
                >
                  {isVideoPlayable(isAccountTrial ?? false, selectedVideo.video.isTrial ?? false, (selectedVideo.video.videoUrl ?? '')  || (selectedVideo.video.videoUrl1 ?? '') || (selectedVideo.video.videoUrl2 ?? '')) ? (
                    <PinnacleChip
                      label="Play"
                      size="medium"
                      sx={{ pointerEvents: 'auto' }}
                      coverButton={true}
                      onClick={() => watchVideoRedirect(
                        navigate,
                        selectedVideo.video,
                        isVideoPlayable(isAccountTrial ?? false, selectedVideo.video.isTrial ?? false, (selectedVideo.video.videoUrl ?? '')  || (selectedVideo.video.videoUrl1 ?? '') || (selectedVideo.video.videoUrl2 ?? ''))
                      )}
                    />
                  ) : (
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 56,
                        color: theme.palette.common.white,
                        borderRadius: '50%',
                        padding: 1,
                        mb: 1,
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Video title and duration */}
              <Box mt={2} mb={2}>
                <Typography
                  className="ellipsis"
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 0.5,
                  }}
                >
                  {selectedVideo.video.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 400, fontSize: '12px', mt: 1 }}
                >
                  {selectedVideo.video.duration}
                </Typography>
              </Box>

              {/* Sort buttons*/}
              {videosState.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{
                      color: Boolean(anchorEl) ? theme.palette.common.black : theme.palette.common.white,
                      backgroundColor: Boolean(anchorEl) ? 'secondary.main' : 'transparent',
                      '&:hover': {
                        backgroundColor: Boolean(anchorEl) ? 'secondary.main' : 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    <SortOutlinedIcon fontSize="small" />
                  </IconButton>
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                    <MenuItem onClick={() => handleSort('createdAt', 'DESC')}>Sort by Date</MenuItem>
                    <MenuItem onClick={() => handleSort('title', 'ASC')}>Sort by Name</MenuItem>
                  </Menu>
                </Box>
              )}

              {/* Related videos list */}
              <Stack spacing={2} pt={2}>
                {isSorting ? (
                  <SkeletonVideoList />
                ) : (
                  videosState.map((video, index) => {
                    const playable = isVideoPlayable(isAccountTrial ?? false, video.isTrial ?? false, (video.videoUrl ?? '') || (video.videoUrl1 ?? '') || (video.videoUrl2 ?? ''));
                    return (
                      <Box
                        key={index}
                        display="flex"
                        gap={1}
                        flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
                        alignItems="flex-start"
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            width: { xs: 140, sm: 180 },
                            height: { xs: 90, sm: 110 },
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            component="img"
                            src={video.thumbnail}
                            alt="thumbnail"
                            onClick={() => playable && watchVideoRedirect(navigate, video, playable)}
                            sx={{
                              borderRadius: '12px',
                              objectFit: 'cover',
                              width: '100%',
                              height: '100%',
                              maxWidth: '100%',
                              flexShrink: 0,
                              filter: !playable ? 'brightness(0.6)' : 'none',
                              transition: 'filter 0.2s',
                              cursor: playable ? 'pointer' : 'default'
                            }}
                          />
                          {!playable && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'rgba(0, 0, 0, 0.6)',
                                zIndex: 2,
                                pointerEvents: 'none',
                                borderRadius: 'inherit',
                              }}
                            >
                              <LockOutlinedIcon
                                sx={{
                                  fontSize: 45,
                                  color: theme.palette.common.white,
                                  borderRadius: '50%',
                                  padding: 0.5,
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                        <Box flex={1} minWidth={0} display="flex" flexDirection="column" justifyContent="center">
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography
                              className="ellipsis"
                              variant="h6"
                              sx={{
                                pr: 1,
                                wordBreak: 'break-word',
                                textAlign: 'left',
                                minWidth: 0,
                                cursor: playable ? 'pointer' : 'default'
                              }}
                              onClick={() => playable && watchVideoRedirect(navigate, video, playable)}
                            >
                              {video.title}
                            </Typography>
                            {playable && (
                              <MarkAsWatchedButton
                                videoId={video._id ?? ''}
                                isWatched={playable ? (video.isWatched ?? false) : false}
                                onChange={(watched) => {
                                  if (playable) {
                                    setVideosState((prev) =>
                                      prev.map((v, i) =>
                                        i === index ? { ...v, isWatched: watched } : v
                                      )
                                    );
                                  } else {
                                    pushNotification({
                                      details: E_CANNOT_MARK_AS_WATCHED,
                                      type: NotificationTypes.Info,
                                    });
                                  }
                                }}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" sx={{ textAlign: 'left', fontWeight: 400, fontSize: '12px' }}>
                            {video.duration}
                          </Typography>
                          {playable && (
                            <Box mt="6px" display="flex" justifyContent="flex-start">
                              <PinnacleChip
                                label="Play"
                                size="small"
                                sx={{ fontWeight: '500' }}
                                onClick={() => watchVideoRedirect(navigate, video, playable)}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Stack>
            </>
          )}
        </Box>
      </PinnacleDialog>
    </>
  );
};

export default VideoListItem;
